import Phaser from "phaser";
import { GAME } from "../config";
import { getMpClient } from "../net/MultiplayerClient";
import {
  setStoredCredentials, clearStoredCredentials,
  validateLogin,
  getStoredPlayerSlug, setStoredPlayerSlug,
  type PlayerSlug,
} from "../net/playerIdentity";
import { preloadAllPacks } from "../fighters/loader";
import { addFullscreenButton } from "../ui/Fullscreen";
import { syncDomContainerScale } from "../ui/domScale";

/**
 * Сцена «Настройки» / «Аккаунт».
 *
 *   Не авторизован:
 *     ┌─ ВОЙТИ ─────────────────┐
 *     │ Логин: [______________] │
 *     │ Пароль:[______________] │
 *     │ [   ВОЙТИ   ]           │
 *     └─────────────────────────┘
 *     ┌─ РЕГИСТРАЦИЯ ───────────┐
 *     │ Логин: [______________] │
 *     │ Пароль:[______________] │
 *     │ [   ЗАРЕГИСТРИРОВАТЬ ]  │
 *     └─────────────────────────┘
 *
 *   Авторизован:
 *     Привет, {login}!  W: ?  L: ?
 *     [Сменить логин]  [Сменить пароль]  [Выйти из аккаунта]
 */
export class SettingsScene extends Phaser.Scene {
  private mp = getMpClient();
  private mpUnsub?: () => void;
  private statusText!: Phaser.GameObjects.Text;
  private domElems: Phaser.GameObjects.DOMElement[] = [];
  private uiNodes: Phaser.GameObjects.GameObject[] = [];
  private requiredLogin = false;
  private modal: "login" | "register" | "changeLogin" | "changePassword" | null = null;

  constructor() { super("Settings"); }

  init(data: { required?: boolean }) {
    this.requiredLogin = !!data?.required;
  }

  preload() {
    // Нужны спрайты для мини-портретов выбора бойца
    preloadAllPacks(this);
  }

  create() {
    const { WIDTH, HEIGHT } = GAME;
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    this.add.text(WIDTH / 2, 28, "⚙ НАСТРОЙКИ", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    const backBtn = this.add.text(60, 28, "← НАЗАД", {
      fontFamily: "Arial Black", fontSize: "14px",
      color: "#ffffff", backgroundColor: "#444444",
      padding: { x: 12, y: 6 } as any,
    } as any).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    backBtn.on("pointerdown", () => this.exit());
    this.input.keyboard!.on("keydown-ESC", () => this.exit());

    addFullscreenButton(this);

    this.statusText = this.add.text(WIDTH / 2, 60, "", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffd700",
    }).setOrigin(0.5);

    this.mpUnsub = this.mp.on(e => this.handleEvent(e));

    if (!this.mp.isOpen()) {
      this.statusText.setText("Соединение...");
      this.mp.connect()
        .then(() => this.renderRoot())
        .catch(() => this.statusText.setColor("#ff4444").setText("Сервер недоступен"));
    } else {
      this.renderRoot();
    }
  }

  private renderRoot() {
    this.clearUI();
    if (this.mp.isAuthed()) this.renderAuthed();
    else this.renderLogin();
  }

  private clearUI() {
    for (const d of this.domElems) d.destroy();
    for (const n of this.uiNodes) n.destroy();
    this.domElems = []; this.uiNodes = [];
  }

  // ── Не авторизован: 2 секции ──────────────────────────────────────────────
  private renderLogin() {
    const { WIDTH } = GAME;
    if (this.requiredLogin) {
      this.statusText.setColor("#ff8888");
      this.statusText.setText("Чтобы играть онлайн — войди или зарегистрируйся");
    }

    this.section(WIDTH * 0.27, 110, "ВОЙТИ", "ВОЙТИ", 0x2a4a8a, "doLogin");
    this.section(WIDTH * 0.73, 110, "РЕГИСТРАЦИЯ", "СОЗДАТЬ", 0x2a6a2a, "doRegister");
  }

  private section(cx: number, y0: number, title: string, btnLabel: string, color: number, action: "doLogin" | "doRegister") {
    const cardW = 380, cardH = 240;
    const card = this.add.rectangle(cx, y0 + cardH / 2, cardW, cardH, 0x1a0505)
      .setStrokeStyle(3, 0xffd700);
    const t = this.add.text(cx, y0 + 18, title, {
      fontFamily: "Arial Black", fontSize: "20px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 3,
    }).setOrigin(0.5);
    this.uiNodes.push(card, t);

    this.add.text(cx - cardW / 2 + 20, y0 + 60, "Логин:", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
    }).setOrigin(0, 0.5);
    const loginInput = this.makeInput(cx - cardW / 2 + 90, y0 + 60, 220, "text", "");

    this.add.text(cx - cardW / 2 + 20, y0 + 100, "Пароль:", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
    }).setOrigin(0, 0.5);
    const passInput = this.makeInput(cx - cardW / 2 + 90, y0 + 100, 220, "password", "");

    const btn = this.add.rectangle(cx, y0 + 170, 200, 44, color)
      .setStrokeStyle(3, 0xffd700)
      .setInteractive({ useHandCursor: true });
    const btnT = this.add.text(cx, y0 + 170, btnLabel, {
      fontFamily: "Arial Black", fontSize: "18px",
      color: "#ffffff", stroke: "#000", strokeThickness: 3,
    }).setOrigin(0.5);
    this.uiNodes.push(btn, btnT);

    const note = this.add.text(cx, y0 + 210, "", {
      fontFamily: "Arial", fontSize: "11px", color: "#888888",
    }).setOrigin(0.5);
    this.uiNodes.push(note);

    btn.on("pointerdown", () => {
      const login = (loginInput.node as HTMLInputElement).value.trim();
      const pass = (passInput.node as HTMLInputElement).value;
      const err = validateLogin(login);
      if (err) { note.setColor("#ff8888").setText(err); return; }
      if (!pass) { note.setColor("#ff8888").setText("Пароль не может быть пустым"); return; }
      note.setColor("#88ccff").setText("Отправка...");
      if (action === "doLogin") this.mp.loginUser(login, pass);
      else                       this.mp.register(login, pass);
      // Запоминаем для авто-логина при следующем заходе — но только после успеха в handleEvent
      this.pendingCredentials = { login, password: pass };
    });
  }

  private pendingCredentials?: { login: string; password: string };

  // ── Авторизован: профиль и кнопки ─────────────────────────────────────────
  private renderAuthed() {
    const { WIDTH } = GAME;
    const cy = 120;
    this.add.text(WIDTH / 2, cy, `ПРИВЕТ, ${this.mp.login!.toUpperCase()}`, {
      fontFamily: "Arial Black", fontSize: "32px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    this.add.text(WIDTH / 2, cy + 38, `Побед: ${this.mp.wins}    Поражений: ${this.mp.losses}`, {
      fontFamily: "Arial Black", fontSize: "16px", color: "#ffffff",
    }).setOrigin(0.5);

    this.button(WIDTH / 2 - 220, cy + 110, "СМЕНИТЬ ЛОГИН",  () => this.showChangeLogin());
    this.button(WIDTH / 2,        cy + 110, "СМЕНИТЬ ПАРОЛЬ", () => this.showChangePassword());
    this.button(WIDTH / 2 + 220, cy + 110, "ВЫЙТИ",          () => this.logout());

    // ── Выбор бойца ───────────────────────────────────────────────────────
    const pickY = cy + 200;
    this.add.text(WIDTH / 2, pickY, "ВЫБЕРИ БОЙЦА", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#aaaaaa",
    }).setOrigin(0.5);

    const slugs: PlayerSlug[] = ["boxer_leo", "boxer_mike", "boxer_don", "boxer_raph"];
    const cellW = 120, cellH = 150, gap = 10;
    const total = slugs.length * cellW + (slugs.length - 1) * gap;
    const x0 = WIDTH / 2 - total / 2 + cellW / 2;

    const renderPickerCells = () => {
      this.pickerNodes.forEach(n => n.destroy());
      this.pickerNodes = [];
      const current = getStoredPlayerSlug();

      slugs.forEach((slug, i) => {
        const x = x0 + i * (cellW + gap);
        const isCurrent = slug === current;
        const fill = isCurrent ? 0x2a1f08 : 0x111111;
        const stroke = isCurrent ? 0xffd700 : 0x444444;
        const strokeW = isCurrent ? 4 : 2;

        const frame = this.add.rectangle(x, pickY + 30 + cellH / 2, cellW, cellH, fill)
          .setStrokeStyle(strokeW, stroke)
          .setInteractive({ useHandCursor: true });
        const sprite = this.add.image(x, pickY + 30 + cellH / 2, `fighter:${slug}`, 0)
          .setScale(0.62);

        frame.on("pointerdown", () => {
          setStoredPlayerSlug(slug);
          this.statusText.setColor("#88ff88").setText("✓ Боец выбран");
          renderPickerCells();
        });

        this.pickerNodes.push(frame, sprite);
        this.uiNodes.push(frame, sprite);
      });
    };
    renderPickerCells();
  }

  private pickerNodes: Phaser.GameObjects.GameObject[] = [];

  private button(cx: number, cy: number, label: string, on: () => void, color = 0x2a4a8a) {
    const btn = this.add.rectangle(cx, cy, 200, 44, color)
      .setStrokeStyle(2, 0xffd700)
      .setInteractive({ useHandCursor: true });
    const txt = this.add.text(cx, cy, label, {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
    }).setOrigin(0.5);
    btn.on("pointerdown", on);
    this.uiNodes.push(btn, txt);
  }

  private showChangeLogin() {
    this.clearUI(); this.modal = "changeLogin";
    const { WIDTH, HEIGHT } = GAME;
    const cx = WIDTH / 2, cy = HEIGHT / 2 - 40;
    this.add.text(cx, cy - 80, "СМЕНИТЬ ЛОГИН", {
      fontFamily: "Arial Black", fontSize: "22px", color: "#ffd700",
    }).setOrigin(0.5);

    this.add.text(cx - 130, cy - 20, "Новый логин:", { fontFamily: "Arial Black", fontSize: "13px", color: "#fff" }).setOrigin(0, 0.5);
    const newL = this.makeInput(cx - 30, cy - 20, 240, "text", this.mp.login || "");
    this.add.text(cx - 130, cy + 20, "Пароль:", { fontFamily: "Arial Black", fontSize: "13px", color: "#fff" }).setOrigin(0, 0.5);
    const pw = this.makeInput(cx - 30, cy + 20, 240, "password", "");

    const note = this.add.text(cx, cy + 110, "", { fontFamily: "Arial", fontSize: "12px", color: "#888" }).setOrigin(0.5);
    this.uiNodes.push(note);

    this.button(cx - 110, cy + 70, "ПРИМЕНИТЬ", () => {
      const v = (newL.node as HTMLInputElement).value.trim();
      const p = (pw.node as HTMLInputElement).value;
      const err = validateLogin(v);
      if (err) { note.setColor("#ff8888").setText(err); return; }
      this.mp.updateLogin(v, p);
      this.pendingCredentials = { login: v, password: p };
    }, 0x2a6a2a);
    this.button(cx + 110, cy + 70, "ОТМЕНА", () => this.renderRoot(), 0x6a2a2a);
  }

  private showChangePassword() {
    this.clearUI(); this.modal = "changePassword";
    const { WIDTH, HEIGHT } = GAME;
    const cx = WIDTH / 2, cy = HEIGHT / 2 - 40;
    this.add.text(cx, cy - 80, "СМЕНИТЬ ПАРОЛЬ", {
      fontFamily: "Arial Black", fontSize: "22px", color: "#ffd700",
    }).setOrigin(0.5);

    this.add.text(cx - 130, cy - 20, "Старый пароль:", { fontFamily: "Arial Black", fontSize: "13px", color: "#fff" }).setOrigin(0, 0.5);
    const oldP = this.makeInput(cx - 30, cy - 20, 240, "password", "");
    this.add.text(cx - 130, cy + 20, "Новый пароль:", { fontFamily: "Arial Black", fontSize: "13px", color: "#fff" }).setOrigin(0, 0.5);
    const newP = this.makeInput(cx - 30, cy + 20, 240, "password", "");

    const note = this.add.text(cx, cy + 110, "", { fontFamily: "Arial", fontSize: "12px", color: "#888" }).setOrigin(0.5);
    this.uiNodes.push(note);

    this.button(cx - 110, cy + 70, "ПРИМЕНИТЬ", () => {
      const o = (oldP.node as HTMLInputElement).value;
      const n = (newP.node as HTMLInputElement).value;
      if (!n) { note.setColor("#ff8888").setText("Новый пароль пуст"); return; }
      this.mp.updatePassword(o, n);
      this.pendingCredentials = { login: this.mp.login!, password: n };
    }, 0x2a6a2a);
    this.button(cx + 110, cy + 70, "ОТМЕНА", () => this.renderRoot(), 0x6a2a2a);
  }

  private logout() {
    clearStoredCredentials();
    this.mp.playerId = undefined;
    this.mp.login = undefined;
    this.mp.wins = 0; this.mp.losses = 0;
    this.mp.friends = []; this.mp.opponents = [];
    // не закрываем WS, просто перестаём быть авторизованными на этом ws (на стороне сервера сесия живёт)
    // На стороне сервера — нет «logout», но при reconnect/refresh — login сбросится.
    this.renderRoot();
  }

  private makeInput(x: number, y: number, w: number, type: string, value: string): Phaser.GameObjects.DOMElement {
    const el = document.createElement("input");
    el.type = type; el.value = value;
    el.maxLength = type === "text" ? 16 : 64;
    el.autocomplete = type === "password" ? "current-password" : "off";
    el.style.cssText =
      `width:${w}px;height:32px;font-size:16px;padding:6px 10px;` +
      `font-family:'Arial Black',sans-serif;color:#ffd700;` +
      `background:#0a0303;border:2px solid #c41e3a;border-radius:4px;` +
      `outline:none;text-align:center;letter-spacing:1px;`;
    const dom = this.add.dom(x, y, el).setOrigin(0, 0.5);
    this.domElems.push(dom);
    // форс пересчёт DOM-container transform — observer тоже сработает, но
    // прямой вызов гарантирует что input не «уедет» в первый кадр.
    syncDomContainerScale();
    this.time.delayedCall(50, syncDomContainerScale);
    return dom;
  }

  private handleEvent(e: any) {
    switch (e.type) {
      case "registered":
      case "logged_in":
        if (this.pendingCredentials) {
          setStoredCredentials(this.pendingCredentials.login, this.pendingCredentials.password);
          this.pendingCredentials = undefined;
        }
        this.statusText.setColor("#88ff88").setText(`✓ Привет, ${e.login}!`);
        this.renderRoot();
        break;
      case "login_updated":
        if (this.pendingCredentials) {
          setStoredCredentials(this.pendingCredentials.login, this.pendingCredentials.password);
          this.pendingCredentials = undefined;
        }
        this.statusText.setColor("#88ff88").setText(`✓ Логин теперь: ${e.login}`);
        this.renderRoot();
        break;
      case "password_updated":
        if (this.pendingCredentials) {
          setStoredCredentials(this.pendingCredentials.login, this.pendingCredentials.password);
          this.pendingCredentials = undefined;
        }
        this.statusText.setColor("#88ff88").setText("✓ Пароль изменён");
        this.renderRoot();
        break;
      case "error":
        this.statusText.setColor("#ff4444").setText(`✗ ${e.msg}`);
        this.pendingCredentials = undefined;
        break;
    }
  }

  private exit() {
    if (this.requiredLogin && !this.mp.isAuthed()) {
      // не дадим выйти без авторизации, если её требуют
      this.statusText.setColor("#ff8888").setText("Сначала войди или зарегистрируйся");
      return;
    }
    this.mpUnsub?.();
    this.scene.start("MainMenu");
  }
}
